import { BriefcaseIcon, CalendarIcon, UserIcon } from "lucide-react";
import { parseAsStringLiteral, useQueryState } from "nuqs";

import { Badge } from "@/components/ui/badge";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { Id } from "../../../../convex/_generated/dataModel";
import { MatterActivities } from "./matter-activities";
import { MatterCalendar } from "./matter-calendar";
import { MatterCommunications } from "./matter-communications";
import { MatterContacts } from "./matter-contacts";
import { MatterDocuments } from "./matter-documents";
import { MatterNotes } from "./matter-notes";
import { MatterOverview } from "./matter-overview";
import { MatterTasks } from "./matter-tasks";
import type { ClioMatterDetail } from "./types";

interface MatterDetailProps {
	connectionId: Id<"oauthConnections">;
	matter: ClioMatterDetail | null;
	isLoading: boolean;
}

const MATTER_TAB_VALUES = [
	"overview",
	"notes",
	"documents",
	"communications",
	"calendar",
	"tasks",
	"activities",
	"contacts",
] as const;
type MatterTab = (typeof MATTER_TAB_VALUES)[number];

const STATUS_BADGE_CLASS: Record<"Open" | "Pending" | "Closed", string> = {
	Open: "bg-success/15 text-success border-transparent",
	Pending: "bg-warning/15 text-warning border-transparent",
	Closed: "bg-muted text-muted-foreground border-transparent",
};

export function MatterDetail({
	connectionId,
	matter,
	isLoading,
}: MatterDetailProps) {
	const [tab, setTab] = useQueryState<MatterTab>(
		"tab",
		parseAsStringLiteral(MATTER_TAB_VALUES).withDefault("overview"),
	);

	if (isLoading) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading matter…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}
	if (!matter) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>
						Select a matter to see its details.
					</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	const headline = matter.displayNumber || `Matter ${matter.clioId}`;
	const subline = matter.description ?? null;

	const countBadge = (n: number): string => (n >= 1000 ? "1000+" : String(n));

	return (
		<div className="flex h-full min-h-0 flex-col">
			<div className="flex flex-col gap-3 px-6 py-5">
				<div className="flex flex-wrap items-center gap-2">
					<h2 className="text-xl font-semibold leading-tight">{headline}</h2>
					<Badge
						className={cn(
							"rounded-full px-2.5 py-0.5 text-xs",
							STATUS_BADGE_CLASS[matter.status],
						)}
					>
						{matter.status}
					</Badge>
					{matter.matterStage && (
						<Badge variant="secondary" className="rounded-full font-normal">
							{matter.matterStage}
						</Badge>
					)}
					{matter.practiceAreaName && (
						<Badge variant="outline" className="rounded-full font-normal">
							{matter.practiceAreaName}
						</Badge>
					)}
				</div>
				{subline && <p className="text-sm text-muted-foreground">{subline}</p>}
				<div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
					{matter.clientName && (
						<span className="inline-flex items-center gap-1.5">
							<UserIcon className="h-3.5 w-3.5" />
							<span className="font-medium text-foreground/80">
								{matter.clientName}
							</span>
						</span>
					)}
					{matter.responsibleAttorneyName && (
						<span className="inline-flex items-center gap-1.5">
							<BriefcaseIcon className="h-3.5 w-3.5" />
							{matter.responsibleAttorneyName}
						</span>
					)}
					{matter.openDate && (
						<span className="inline-flex items-center gap-1.5">
							<CalendarIcon className="h-3.5 w-3.5" />
							Opened {matter.openDate}
						</span>
					)}
					{matter.closeDate && (
						<span className="inline-flex items-center gap-1.5">
							<CalendarIcon className="h-3.5 w-3.5" />
							Closed {matter.closeDate}
						</span>
					)}
				</div>
			</div>
			<Separator />
			<Tabs
				value={tab}
				onValueChange={(v) => {
					if ((MATTER_TAB_VALUES as readonly string[]).includes(v)) {
						setTab(v as MatterTab);
					}
				}}
				className="flex min-h-0 flex-1 flex-col gap-0"
			>
				<div className="px-6 py-2">
					<TabsList>
						<TabsTrigger value="overview">Overview</TabsTrigger>
						<TabsTrigger value="notes">
							Notes ({countBadge(matter.counts.notes)})
						</TabsTrigger>
						<TabsTrigger value="documents">
							Documents ({countBadge(matter.counts.documents)})
						</TabsTrigger>
						<TabsTrigger value="communications">
							Comms ({countBadge(matter.counts.communications)})
						</TabsTrigger>
						<TabsTrigger value="calendar">
							Calendar ({countBadge(matter.counts.calendarEntries)})
						</TabsTrigger>
						<TabsTrigger value="tasks">
							Tasks ({countBadge(matter.counts.tasks)})
						</TabsTrigger>
						<TabsTrigger value="activities">
							Activities ({countBadge(matter.counts.activities)})
						</TabsTrigger>
						<TabsTrigger value="contacts">
							Contacts ({countBadge(matter.counts.contacts)})
						</TabsTrigger>
					</TabsList>
				</div>
				<Separator />
				<div className="flex min-h-0 flex-1 flex-col">
					<TabsContent value="overview" className="m-0 flex-1 overflow-hidden">
						<ScrollArea className="h-full">
							<MatterOverview connectionId={connectionId} matter={matter} />
						</ScrollArea>
					</TabsContent>
					<TabsContent value="notes" className="m-0 flex-1 overflow-hidden">
						<MatterNotes
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
					<TabsContent value="documents" className="m-0 flex-1 overflow-hidden">
						<MatterDocuments
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
					<TabsContent
						value="communications"
						className="m-0 flex-1 overflow-hidden"
					>
						<MatterCommunications
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
					<TabsContent value="calendar" className="m-0 flex-1 overflow-hidden">
						<MatterCalendar
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
					<TabsContent value="tasks" className="m-0 flex-1 overflow-hidden">
						<MatterTasks
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
					<TabsContent
						value="activities"
						className="m-0 flex-1 overflow-hidden"
					>
						<MatterActivities
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
					<TabsContent value="contacts" className="m-0 flex-1 overflow-hidden">
						<MatterContacts
							connectionId={connectionId}
							clioMatterId={matter.clioId}
						/>
					</TabsContent>
				</div>
			</Tabs>
		</div>
	);
}
